import { ConfigService } from '../../infra/config'
import { BaseRepository } from '../../infra/base.repository'
import type { Purchase } from './purchase.entity'
import type { CreateInvoiceDto } from './dto/create-invoice.dto'
import type { AssignNewCardDto } from '../customer-cards/dto/assign-new-card.dto'
import type { ChangePurchaseDueDateInputDto, ChangePurchaseDueDateOutputDto, ChangePurchaseValueInputDto } from './dto/change-purchase-due-date.dto'
import type { RevokeScheduledCancelOutputDto, ScheduleCancelInputDto, ScheduleCancelOutputDto } from './dto/schedule-cancel.dto'
import type { ChangeGatewayInputDto, ChangeGatewayOutputDto } from './dto/change-gateway.dto'
import type { ChangeMethodInputDto, ChangeMethodOutputDto } from './dto/change-method.dto'
import type { PayerDto, UpdatePayerDto } from './dto/payer.dto'
import type { ApplyPriceAdjustmentDto, PriceAdjustmentConfigDto, PriceAdjustmentPreviewDto, RevertPriceAdjustmentPreviewDto, PriceIndex } from './dto/price-adjustment.dto'

export class PurchasesRepository extends BaseRepository<Purchase> {
    constructor(
        private config: ConfigService = new ConfigService(),
        token?: string,
        cache?: any,
    ) {
        super(config.paymentApiUrl + '/purchases', token, cache)
    }

    async createInvoice(dto: CreateInvoiceDto) {
        const { json, statusCode, message } = await this.request.post(this.config.paymentApiUrl + '/purchases?token=' + this.resolvedToken, dto)
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async updateCard(purchaseId: string, dto: AssignNewCardDto) {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/card?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async changeDueDate(purchaseId: string, dto: ChangePurchaseDueDateInputDto): Promise<ChangePurchaseDueDateOutputDto> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/change-due-date?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async changeValue(purchaseId: string, dto: ChangePurchaseValueInputDto): Promise<{ success: boolean }> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/change-value?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async changeGateway(purchaseId: string, dto: ChangeGatewayInputDto): Promise<ChangeGatewayOutputDto> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/change-gateway?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async getPayer(purchaseId: string): Promise<PayerDto> {
        const { json, statusCode, message } = await this.request.get(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/payer?token=' + this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async updatePayer(purchaseId: string, dto: UpdatePayerDto): Promise<PayerDto> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/payer?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async changeMethod(purchaseId: string, dto: ChangeMethodInputDto): Promise<ChangeMethodOutputDto> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/change-method?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async scheduleCancel(purchaseId: string, dto: ScheduleCancelInputDto): Promise<ScheduleCancelOutputDto> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/schedule-cancel?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async revokeScheduledCancel(purchaseId: string): Promise<RevokeScheduledCancelOutputDto> {
        const { json, statusCode, message } = await this.request.delete(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/schedule-cancel?token=' + this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async getOnePublic(purchaseId: string, charges: boolean = false) {
        const { json, statusCode, message } = await this.request.get(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/public?charges=' + charges + '&token=' + this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async billIntegration(purchaseId: string, dto: { billIntegration: boolean }) {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/bill-integration?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    // ##### reajuste de preço por índice contratual #####

    async setPriceAdjustmentConfig(purchaseId: string, dto: PriceAdjustmentConfigDto) {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/price-adjustment/config?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async previewPriceAdjustment(purchaseId: string, index?: PriceIndex): Promise<PriceAdjustmentPreviewDto> {
        const query = index ? '&index=' + index : ''
        const { json, statusCode, message } = await this.request.get(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/price-adjustment/preview?token=' + this.resolvedToken + query,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async applyPriceAdjustment(purchaseId: string, dto: ApplyPriceAdjustmentDto): Promise<PriceAdjustmentPreviewDto> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/price-adjustment/apply?token=' + this.resolvedToken,
            dto,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async previewRevertPriceAdjustment(purchaseId: string): Promise<RevertPriceAdjustmentPreviewDto> {
        const { json, statusCode, message } = await this.request.get(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/price-adjustment/revert-preview?token=' + this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async revertPriceAdjustment(purchaseId: string): Promise<RevertPriceAdjustmentPreviewDto> {
        const { json, statusCode, message } = await this.request.post(
            this.config.paymentApiUrl + '/purchases/' + purchaseId + '/price-adjustment/revert?token=' + this.resolvedToken,
            {},
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }
}
